import React from 'react';
import PropTypes from 'prop-types';
import translate from '../../../../commonComponents/translate';
import PlanProp from '../../propTypes/PlanProp';
import {
  getMaximumRangeAllowed,
  getPriceForUsers,
} from '../../../../services/cartCalculations';
import { displayMoney } from '../../../../services/utilityFunctions';

function Plans(props) {
  const {
    t,
    plans,
    currentUserStats,
    interval,
    readOnly,
    selectedPlan,
    onPlanChanged,
    onUserCalculationsChanged,
    calculatedUserStats,
    shouldCalculateActiveUsers,
    hasPermissionToChange,
  } = props;

  const { tiersUsers, tiersAdmins } = selectedPlan;

  function changeHandler(type) {
    return ({ currentTarget }) => {
      if (currentTarget.value) {
        onUserCalculationsChanged(type, Number(currentTarget.value));
      } else {
        onUserCalculationsChanged(type, 1);
      }
    };
  }

  const { total: currentTotalPriceUsers } = getPriceForUsers(
    tiersUsers,
    currentUserStats.totalUsers,
  );
  const { total: currentTotalPriceAdmins } = getPriceForUsers(
    tiersAdmins,
    currentUserStats.totalAdmins,
  );

  const maximumRangeUsers = getMaximumRangeAllowed(tiersUsers);
  const maximumRangeAdmins = getMaximumRangeAllowed(tiersAdmins);

  const {
    price: calculatedPriceUsers,
    isFlat: isFlatPriceForUsers,
    total: calculatedTotalPriceUsers,
  } = getPriceForUsers(tiersUsers, calculatedUserStats.totalUsers);
  const {
    price: calculatedPriceAdmin,
    isFlat: isFlatPriceForAdmins,
    total: calculatedTotalPriceAdmins,
  } = getPriceForUsers(tiersAdmins, calculatedUserStats.totalAdmins);

  const priceLabelUsers = isFlatPriceForUsers
    ? `${displayMoney(calculatedPriceUsers)}/${t(`interval_${interval}`)}`
    : `${
        shouldCalculateActiveUsers
          ? t('subscriptionCreate_perActiveUser', t(`interval_${interval}`))
          : t('subscriptionCreate_perDistributor', t(`interval_${interval}`))
      }\n${displayMoney(calculatedPriceUsers)}`;

  const priceLabelAdmins = isFlatPriceForAdmins
    ? `${displayMoney(calculatedPriceAdmin)}/${t(`interval_${interval}`)}`
    : `${t(
        'subscriptionCreate_perAdmin',
        t(`interval_${interval}`),
      )}\n${displayMoney(calculatedPriceAdmin)}`;

  return (
    <div className="plans-container">
      <div className="plans-calculator">
        <h3>{t('subscriptionCreate_plans')}</h3>
        <div className="plans-selector-container">
          {plans.map(plan => (
            <button
              type="button"
              key={plan._id}
              className={`plans-selector-item ${
                selectedPlan?._id === plan._id ? 'selected' : ''
              }`}
              onClick={() => onPlanChanged(plan)}
              disabled={!hasPermissionToChange}
            >
              {plan.name}
            </button>
          ))}
        </div>
        <div className="plans-price-users-container">
          <h4>
            {shouldCalculateActiveUsers
              ? t('subscriptionCreate_users')
              : t('subscriptionCreate_distributors')}
          </h4>
          <div className="plan-price-container">
            <input
              className="plan-users-input"
              value={calculatedUserStats.totalUsers}
              onChange={changeHandler('users')}
              disabled={readOnly || maximumRangeUsers < 600}
            />
            <span className="plan-price">{priceLabelUsers}</span>
          </div>
          <input
            type="range"
            value={calculatedUserStats.totalUsers}
            min={1}
            max={maximumRangeUsers < 600 ? maximumRangeUsers : 600}
            onChange={changeHandler('users')}
            disabled={readOnly}
          />
        </div>
        {tiersAdmins.length > 0 && (
          <div className="plans-price-users-container">
            <h4>{t('subscriptionCreate_administrators')}</h4>
            <div className="plan-price-container">
              <input
                className="plan-users-input"
                value={calculatedUserStats.totalAdmins}
                onChange={changeHandler('admins')}
                disabled={readOnly || maximumRangeAdmins < 50}
              />
              <span className="plan-price">{priceLabelAdmins}</span>
            </div>
            <input
              type="range"
              value={calculatedUserStats.totalAdmins}
              min={1}
              max={maximumRangeAdmins < 50 ? maximumRangeAdmins : 50}
              onChange={changeHandler('admins')}
              disabled={readOnly}
            />
          </div>
        )}
      </div>
      <div className="billing-container">
        <div className="billing-top">
          <h5>{t('subscriptionCreate_estimated_cost')}</h5>
          <span className="billing-price">
            {displayMoney(currentTotalPriceUsers + currentTotalPriceAdmins)}
          </span>
          <h6>{t('subscriptionCreate_billed_last_month')}</h6>
          <span className="billing-price-smaller">
            {displayMoney(currentTotalPriceUsers + currentTotalPriceAdmins)}
          </span>
        </div>
        <div className="billing-bottom">
          <h5>{t('subscriptionCreate_calculated_plan')}</h5>
          <span className="billing-price bottom-aligned">
            {displayMoney(
              calculatedTotalPriceUsers + calculatedTotalPriceAdmins,
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

Plans.propTypes = {
  t: PropTypes.func.isRequired,
  plans: PropTypes.arrayOf(PropTypes.shape(PlanProp.propType)).isRequired,
  selectedPlan: PropTypes.shape(PlanProp.propType),
  onPlanChanged: PropTypes.func.isRequired,
  currentUserStats: PropTypes.shape({
    totalUsers: PropTypes.number,
    totalAdmins: PropTypes.number,
  }).isRequired,
  calculatedUserStats: PropTypes.shape({
    totalUsers: PropTypes.number,
    totalAdmins: PropTypes.number,
  }).isRequired,
  onUserCalculationsChanged: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  interval: PropTypes.string.isRequired,
  shouldCalculateActiveUsers: PropTypes.bool.isRequired,
  hasPermissionToChange: PropTypes.bool.isRequired,
};

Plans.defaultProps = {
  selectedPlan: null,
  readOnly: false,
};

export default translate(Plans);
